import type { TFunction } from 'i18next';
import { z } from 'zod';

export type ContactValues = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
};

export type OrgFormValues = {
  name: string;
  address: string;
  alias: string;
  primaryContact: ContactValues;
  itContact: ContactValues;
  domain: string;
  subscription: {
    active: boolean;
    expiration: string;
  };
};

const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;
const DOMAIN_REGEX = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

export function createOrgSchema(t: TFunction<'orgs'>) {
  const contactSchema = z.object({
    firstName: z.string().min(1, t('validation_first_name_required')),
    lastName: z.string().min(1, t('validation_last_name_required')),
    phone: z.string().regex(PHONE_REGEX, t('validation_phone_invalid')),
    email: z.string().email(t('validation_email_invalid')),
  });

  return z.object({
    name: z.string().min(1, t('validation_name_required')).min(3, t('validation_name_min_length')),
    address: z.string().min(1, t('validation_address_required')),
    alias: z.string().min(1, t('validation_alias_required')).min(2, t('validation_alias_min_length')),
    primaryContact: contactSchema,
    itContact: contactSchema,
    domain: z.string().min(1, t('validation_domain_required')).regex(DOMAIN_REGEX, t('validation_domain_invalid')),
    subscription: z
      .object({
        active: z.boolean(),
        expiration: z.string(),
      })
      .superRefine((val, ctx) => {
        if (val.active && !val.expiration) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('validation_expiration_required'),
            path: ['expiration'],
          });
        }
      }),
  });
}
